import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { isConvertedProOrAdvanced } from 'src/providers/SubscriptionProvider/Subscription.utils';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { LeadAgentAcceptanceContext } from './LeadAgentAcceptance.context';
import {
  daysSincePayment as computeDaysSincePayment,
  daysUntilPayment as computeDaysUntilPayment,
  hasBillableSubscriptionForRevokedLeadAgent,
  isAcceptancePending,
  isInformativeBannerSuppressed,
  isLeadAgentExpired,
  isLeadAgentPaused,
  isLeadAgentPausedAndLapsed,
  isLeadAgentPausedWithinTrial,
  isLeadAgentRevoked,
  resolveBannerVariant,
  resolveExpiredPhase,
  resolvePaidUsageBand,
  resolveSelectableTier,
  resolveTrialCapBannerVariant,
  resolveUsagePhase,
} from './LeadAgentAcceptance.functions';
import { LeadAgentAcceptanceService } from './LeadAgentAcceptance.service';
import { LeadAgentUsageService } from './LeadAgentUsage.service';
import { LeadAgentUnlockOfferService } from './LeadAgentUnlockOffer.service';
import { smartLeadbotConfigService } from 'src/pages/Settings/components/SmartLeadbotConfig/SmartLeadbotConfig.service';
import type {
  tLeadAgentAcceptance,
  tLeadAgentReadStatus,
  tLeadAgentSelectableTier,
  tLeadAgentUnlockOffer,
  tLeadAgentUsage,
} from './LeadAgentAcceptance.types';

/**
 * The status a usage or offer read reports when a run starts or fails. A
 * refresh run over a loaded read keeps reporting it loaded, since its value is
 * kept on error too; any other run reports its own status.
 * @param {object} options - The run and the statuses involved.
 * @param {boolean} options.isRefreshRun - Whether the run is a background refresh.
 * @param {tLeadAgentReadStatus} options.current - The status before this change.
 * @param {tLeadAgentReadStatus} options.next - The status the run reports otherwise.
 * @returns {tLeadAgentReadStatus} The status to publish.
 */
function resolveRunReadStatus(options: {
  isRefreshRun: boolean;
  current: tLeadAgentReadStatus;
  next: tLeadAgentReadStatus;
}): tLeadAgentReadStatus {
  return options.isRefreshRun && options.current === 'loaded'
    ? options.current
    : options.next;
}

/**
 * RET-1230 (C2) — holds the Lead Agent pre-acceptance state for the paywall
 * banner + modals. Fetches only for Owners (the offer, prices and CTAs are
 * Owner-only; the Seller surface is C3 · RET-1231) and treats GET 404 as the
 * contractual "not in the presale cohort / banner hidden" answer.
 *
 * SCOPE (conscious decision): this single provider/context backs ALL Lead Agent
 * banner surfaces — C2 (pre-acceptance), C6 (trial activation), C7 (vencido),
 * and C8 (usage/limit, RET-1344) — because they share the acceptance row, the
 * workspace lifecycle, and the same fetch/role gating, and they are mutually
 * exclusive by design (acceptance-driven C2/C6/C7 vs usage-driven C8, which only
 * fires when there is no acceptance row). Keep new sibling surfaces here for that
 * consistency. Revisit splitting (e.g. a dedicated useLeadAgentUsage hook behind
 * the same context, or per-surface providers) when a 5th surface lands or the
 * usage fetch needs independent caching — C5/RET-1304 is the likely trigger.
 */
export default function LeadAgentAcceptanceProvider(props: {
  children: ReactNode;
}) {
  const { currentWorkspace } = useWorkspaceContext();
  const currentWorkspaceId = currentWorkspace?.id ?? '';
  const { userIsOwner, userIsAdmin } = useACLContext();
  // C7 (RET-1343): the vencido banner shows to OWNER (actionable) and ADMIN
  // (informative), so the provider now fetches for both. SELLER/VIEWER stay
  // silent. The owner-only surfaces (pill, pre-acceptance banner) are protected
  // by owner-scoping their flags below — never by the fetch gate alone.
  const canSeeLeadAgentSurface = userIsOwner || userIsAdmin;
  const { subscription } = useSubscriptionContext();
  // The add-on is only purchasable on Pro/Advanced, and the paywall surfaces
  // (C2/C6/C7/C8) are converts-only: a subscription still in its trial has not
  // paid for the base plan, so it must never see the offer (RET-1525, mirrors
  // the workspace-service guard that withholds the acceptance row for trials).
  // Basic/Legacy and trials both fail closed here.
  const isPlanEligible = isConvertedProOrAdvanced(subscription);
  // RET-2153 (TA-11) — the REVOKED-only cut: whether today's subscription can
  // still be billed for the add-on (ACTIVE or PAST_DUE). See
  // `hasBillableSubscriptionForRevokedLeadAgent`'s docblock for why this is
  // scoped to the revoked path and never applied to PAUSED.
  const hasBillableSubscriptionForRevoke =
    hasBillableSubscriptionForRevokedLeadAgent(
      subscription?.status,
      subscription?.pendingCancellation,
    );

  const [acceptance, setAcceptance] = useState<tLeadAgentAcceptance | null>(
    null,
  );
  const [isLoadingAcceptance, setIsLoadingAcceptance] = useState(false);
  // Session-local "clean dashboard" latch (RET-1230): once the Owner accepts
  // or pauses, no acceptance surface renders again this session. Only ever
  // hides — the next full load re-reads the real backend state.
  const [isResolvedLocally, setIsResolvedLocally] = useState(false);
  // The payment response knows the chosen tier before acceptance (404 after
  // purchase) and usage-cap reads converge. Keep that tier for this workspace
  // session so Settings can immediately show the add-on the owner just bought.
  const [sessionActiveTier, setSessionActiveTier] =
    useState<tLeadAgentSelectableTier | null>(null);
  // RET-1633 (U10): per-workspace optimistic settle for the trial-cap UNLOCK
  // flow. Once the Owner completes an unlock charge the just-paid user must not
  // keep seeing the sighting/capped trial-cap banner this session — the
  // provider's offer refetch lags the webhook that clears the cap. Like
  // `isResolvedLocally` this only ever HIDES; it is reset on every workspace
  // change and the next full load re-reads the real backend state. Scoped to
  // the trial-cap variant, so it never touches the C2/C6/C7/C8 surfaces.
  const [isTrialCapSettledLocally, setIsTrialCapSettledLocally] =
    useState(false);
  // RET-1490: whether the workspace has EVER used the Lead Agent — the bot has
  // responded to >=1 lead, lifetime (`everUsed` from the config endpoint) — NOT
  // the current on/off toggle (`aiRespondEnabled`), which is stale and answers
  // "responding right now?" instead of "ever used?". `null` while unknown (not
  // yet fetched / error / absent / no acceptance) — both the C2 acceptance banner
  // (ever-used) and the C6 activation banner (never-used) fail closed while null.
  const [everUsed, setEverUsed] = useState<boolean | null>(null);
  // C8 (RET-1344): the Lead Agent capacity for the usage/limit banner. `null`
  // while unknown (not yet fetched / error / not applicable) — the usage banner
  // fails closed (hidden) until a real `hasCap` payload lands.
  const [usage, setUsage] = useState<tLeadAgentUsage | null>(null);
  // RET-1633 (U9): the trial-cap unlock offer, fetched only when the usage
  // payload flags the trial-cap cohort (`usage.isTrialCap`). `null` while the
  // fetch is pending / errored / not applicable — the trial-cap banner fails
  // closed (hidden) and the base usage banner renders unchanged until it lands.
  const [offer, setOffer] = useState<tLeadAgentUnlockOffer | null>(null);
  // C8 (RET-1344): true once the acceptance fetch has SETTLED for the current
  // workspace. The usage fetch gates on this (not `!isLoadingAcceptance`, which
  // is ambiguously false on first render BEFORE the acceptance effect flips it)
  // so the usage endpoint is never called during the initial acceptance-load
  // window — only after acceptance has resolved to "absent" (an ACCEPTED
  // workspace 404s). This is what keeps the usage fetch off the C2/C6/C7 cohort.
  const [acceptanceResolved, setAcceptanceResolved] = useState(false);
  const [settledWorkspaceId, setSettledWorkspaceId] = useState<string | null>(null);
  // RET-2329 — bumped once per settled `refreshAcceptance` call (success or
  // error), never on the initial load. Feeds the usage/offer effects' deps so
  // a background refresh re-requests them even when `acceptanceResolved` and
  // `usageIsTrialCap` do not change (KTD1). Reset to 0 on workspace change so
  // the new workspace's first load is still treated as a first load (KTD2).
  const [refreshNonce, setRefreshNonce] = useState(0);
  // How the latest usage and unlock-offer requests settled. The published read
  // statuses add the gates on top (`idle`) and the wait for acceptance
  // (`loading`), so these only describe a request that is allowed to run.
  const [usageRequestStatus, setUsageRequestStatus] =
    useState<tLeadAgentReadStatus>('loading');
  const [offerRequestStatus, setOfferRequestStatus] =
    useState<tLeadAgentReadStatus>('loading');
  // Bumped by `retryTrialCapReads` so the usage and offer effects run again.
  const [retryNonce, setRetryNonce] = useState(0);

  // Mid-await workspace guard: async results only apply if the workspace the
  // request was issued for is still the active one (see
  // docs/solutions/logic-errors/websocket-race-overwrites-guard-state-during-await.md).
  const activeWorkspaceIdRef = useRef(currentWorkspaceId);
  activeWorkspaceIdRef.current = currentWorkspaceId;

  // RET-2329 (KTD3) — one in-flight `refreshAcceptance` per workspace. A
  // second call for the same workspace joins the pending promise instead of
  // firing a duplicate request (R6).
  const inFlightRefreshRef = useRef<{
    workspaceId: string;
    promise: Promise<void>;
  } | null>(null);

  useEffect(() => {
    if (!currentWorkspaceId || !canSeeLeadAgentSurface) {
      setAcceptance(null);
      setIsLoadingAcceptance(false);
      setIsResolvedLocally(false);
      setSessionActiveTier(null);
      setIsTrialCapSettledLocally(false);
      setAcceptanceResolved(false);
      setRefreshNonce(0);
      inFlightRefreshRef.current = null;
      return;
    }

    const signal = { cancelled: false };
    setIsLoadingAcceptance(true);
    setIsResolvedLocally(false);
    setSessionActiveTier(null);
    setIsTrialCapSettledLocally(false);
    // Re-gate the usage fetch on workspace change until acceptance settles again.
    setAcceptanceResolved(false);
    // RET-2329 — a new workspace's first load must be treated as a first
    // load by the usage/offer effects (fail closed on error), not as a
    // continuation of the previous workspace's refresh count.
    setRefreshNonce(0);
    inFlightRefreshRef.current = null;

    LeadAgentAcceptanceService.getAcceptance(currentWorkspaceId)
      .then((row) => {
        if (signal.cancelled) return;
        setAcceptance(row);
      })
      .catch(() => {
        // Real transport/5xx failures (404 already resolves to null inside
        // the service). Fail closed: no banner. AxiosService already logged
        // and captured the error.
        if (signal.cancelled) return;
        setAcceptance(null);
      })
      .finally(() => {
        if (signal.cancelled) return;
        setIsLoadingAcceptance(false);
        setAcceptanceResolved(true);
        setSettledWorkspaceId(currentWorkspaceId);
      });

    return () => {
      signal.cancelled = true;
    };
  }, [currentWorkspaceId, canSeeLeadAgentSurface]);

  // RET-1490: fetch the ever-used signal from the config endpoint, but only once
  // we know the workspace is in the Lead Agent cohort (`acceptance` non-null) and
  // the session can see the surface. Gating on the acceptance row keeps non-cohort
  // owners from ever calling getConfig. On error or no acceptance → null
  // (fail closed: neither the C2 nor the C6 banner renders).
  useEffect(() => {
    if (!currentWorkspaceId || !canSeeLeadAgentSurface || !acceptance) {
      setEverUsed(null);
      return;
    }

    const signal = { cancelled: false };

    smartLeadbotConfigService
      .getConfig(currentWorkspaceId)
      .then((config) => {
        if (signal.cancelled) return;
        // Source the banner gate from the lifetime ever-used signal, not the
        // stale current on/off flag. Absent (undefined) → null = unknown →
        // fail closed (both banners hidden).
        setEverUsed(config.everUsed ?? null);
      })
      .catch(() => {
        if (signal.cancelled) return;
        setEverUsed(null);
      });

    return () => {
      signal.cancelled = true;
    };
    // Depend on the row IDENTITY (`acceptance?.id`), not the object reference.
    // In-place mutations (acceptTier/pause/dismiss replace `acceptance` with a
    // new object) would otherwise re-run this effect, resetting everUsed=null
    // (visible flicker) and refetching getConfig. The body still reads the full
    // `acceptance` for its null guard; only the cohort row identity changing
    // (workspace switch / first load) should re-fetch the ever-used signal.
  }, [currentWorkspaceId, canSeeLeadAgentSurface, acceptance?.id]);

  // C8 (RET-1344) + RET-1633 (U9): fetch the Lead Agent capacity once acceptance
  // has SETTLED (`acceptanceResolved`), for both cohorts:
  //  - the paid cohort (acceptance === null — an ACCEPTED workspace 404s) drives
  //    the C8 usage banner, and
  //  - the trial cohort (acceptance !== null) needs the payload's `isTrialCap`
  //    marker to decide whether to fetch the unlock offer (RET-1633).
  // The broadening is why the gate is `acceptanceResolved` rather than the old
  // `acceptance === null`: trial workspaces DO have an acceptance row, so gating
  // the fetch on its absence would starve the trial-cap path. C8's mutual
  // exclusion with C2/C6/C7 is preserved at the RENDER layer instead — the
  // `usagePhase` below only fires when `acceptance === null`, so an
  // acceptance-present workspace never paints the C8 banner even though its
  // usage is now fetched. Gated on plan eligibility (Pro/Advanced) so non-LA
  // owners never call it; on error → usage null (fail closed). Waiting for
  // `acceptanceResolved` (not `!isLoadingAcceptance`, ambiguously false on first
  // render) closes the initial-window race.
  useEffect(() => {
    if (
      !currentWorkspaceId ||
      !canSeeLeadAgentSurface ||
      !isPlanEligible ||
      !acceptanceResolved
    ) {
      setUsage(null);
      setUsageRequestStatus('loading');
      return;
    }

    const signal = { cancelled: false };
    // RET-2329 (KTD2) — a refresh run (refreshNonce > 0) keeps the previous
    // usage on error instead of blanking it; the first load (nonce 0) still
    // fails closed. Captured at effect-run time, so it does not shift mid-flight.
    const isRefreshRun = refreshNonce > 0;
    setUsageRequestStatus((current) =>
      resolveRunReadStatus({ isRefreshRun, current, next: 'loading' }),
    );

    LeadAgentUsageService.getUsage(currentWorkspaceId)
      .then((result) => {
        if (signal.cancelled) return;
        setUsage(result);
        setUsageRequestStatus('loaded');
      })
      .catch(() => {
        if (signal.cancelled) return;
        setUsageRequestStatus((current) =>
          resolveRunReadStatus({ isRefreshRun, current, next: 'failed' }),
        );
        if (isRefreshRun) return;
        setUsage(null);
      });

    return () => {
      signal.cancelled = true;
    };
  }, [
    currentWorkspaceId,
    canSeeLeadAgentSurface,
    isPlanEligible,
    acceptanceResolved,
    refreshNonce,
    retryNonce,
  ]);

  // RET-1633 (U9): fetch the trial-cap unlock offer ONLY when the usage payload
  // flags the trial-cap cohort (`usage.isTrialCap`) — NOT keyed off the C8
  // no-acceptance predicate (trial workspaces have an acceptance row). Fail
  // closed: while pending / on error / when not the trial cohort, `offer` stays
  // null and the trial-cap banner renders nothing (the base usage banner is
  // unaffected). The boolean dep keys re-fetch off the marker flipping, not on
  // usage-object identity churn.
  const usageIsTrialCap = usage?.isTrialCap === true;
  useEffect(() => {
    if (
      !currentWorkspaceId ||
      !canSeeLeadAgentSurface ||
      !isPlanEligible ||
      !usageIsTrialCap
    ) {
      setOffer(null);
      setOfferRequestStatus('loading');
      return;
    }

    const signal = { cancelled: false };
    // RET-2329 (KTD2) — same refresh-vs-first-load distinction as the usage
    // effect above: a refresh run keeps the previous offer on error.
    const isRefreshRun = refreshNonce > 0;
    setOfferRequestStatus((current) =>
      resolveRunReadStatus({ isRefreshRun, current, next: 'loading' }),
    );

    LeadAgentUnlockOfferService.getOffer(currentWorkspaceId)
      .then((result) => {
        if (signal.cancelled) return;
        setOffer(result);
        setOfferRequestStatus('loaded');
      })
      .catch(() => {
        if (signal.cancelled) return;
        setOfferRequestStatus((current) =>
          resolveRunReadStatus({ isRefreshRun, current, next: 'failed' }),
        );
        if (isRefreshRun) return;
        setOffer(null);
      });

    return () => {
      signal.cancelled = true;
    };
  }, [
    currentWorkspaceId,
    canSeeLeadAgentSurface,
    isPlanEligible,
    usageIsTrialCap,
    refreshNonce,
    retryNonce,
  ]);

  // The published statuses: `idle` when the read does not run for this session,
  // `loading` while usage waits for acceptance to settle, else the request's own
  // settlement. The offer runs only behind a loaded trial-cap usage read, so it
  // is loading while usage is and idle once usage settles as anything else.
  let usageReadStatus: tLeadAgentReadStatus = usageRequestStatus;
  if (!currentWorkspaceId || !canSeeLeadAgentSurface || !isPlanEligible) {
    usageReadStatus = 'idle';
  } else if (!acceptanceResolved) {
    usageReadStatus = 'loading';
  }
  let unlockOfferReadStatus: tLeadAgentReadStatus = 'idle';
  if (usageReadStatus === 'loading') unlockOfferReadStatus = 'loading';
  else if (usageReadStatus === 'loaded' && usageIsTrialCap) {
    unlockOfferReadStatus = offerRequestStatus;
  }

  // Re-running both effects reads usage again, and the offer with it while
  // usage is a trial cap; a usage read that turns into a trial cap requests
  // the offer once it loads.
  const retryTrialCapReads = useCallback(() => {
    setRetryNonce((nonce) => nonce + 1);
  }, []);

  const transition = useCallback(
    async (options: {
      state: 'ACCEPTED' | 'PAUSED';
      tier?: tLeadAgentSelectableTier;
    }): Promise<tLeadAgentAcceptance> => {
      // Capture before the await — context state can change mid-flight.
      const workspaceId = activeWorkspaceIdRef.current;
      if (!workspaceId) {
        throw new Error('No active workspace for lead agent transition');
      }

      const updated = await LeadAgentAcceptanceService.transitionState(
        workspaceId,
        options,
      );

      // Apply only if the user has not switched workspaces mid-await.
      if (activeWorkspaceIdRef.current === workspaceId) {
        setAcceptance(updated);
      }
      return updated;
    },
    [],
  );

  const acceptTier = useCallback(
    (tier?: tLeadAgentSelectableTier) =>
      transition({ state: 'ACCEPTED', tier }),
    [transition],
  );

  const pauseAcceptance = useCallback(
    () => transition({ state: 'PAUSED' }),
    [transition],
  );

  // RET-2329 — refreshing is not switching entities, so this no longer
  // touches `isLoadingAcceptance`/`acceptanceResolved`: doing so vacated
  // usage, offer and the trial-cap flags for the length of the refresh,
  // which unmounted `ConfirmTierModal` (KTD1). A 404 (the service resolves
  // `row: null`) is still a real answer and is written; only a thrown
  // network/5xx error keeps the previous `acceptance` (KTD2). The nonce bump
  // only happens if the workspace has not changed mid-await, so usage/offer
  // are never re-requested for a stale workspace.
  const refreshAcceptance = useCallback(async (): Promise<void> => {
    const workspaceId = activeWorkspaceIdRef.current;
    if (!workspaceId || !canSeeLeadAgentSurface) return;

    if (inFlightRefreshRef.current?.workspaceId === workspaceId) {
      return inFlightRefreshRef.current.promise;
    }

    const promise = (async () => {
      try {
        const row =
          await LeadAgentAcceptanceService.getAcceptance(workspaceId);
        if (activeWorkspaceIdRef.current === workspaceId) {
          setAcceptance(row);
        }
      } catch {
        // KTD2: a refresh error keeps the previous acceptance value.
      } finally {
        if (activeWorkspaceIdRef.current === workspaceId) {
          setRefreshNonce((nonce) => nonce + 1);
        }
        if (inFlightRefreshRef.current?.workspaceId === workspaceId) {
          inFlightRefreshRef.current = null;
        }
      }
    })();

    inFlightRefreshRef.current = { workspaceId, promise };
    return promise;
  }, [canSeeLeadAgentSurface]);

  const markResolved = useCallback(
    (acceptedTier?: tLeadAgentSelectableTier) => {
      setIsResolvedLocally(true);
      if (acceptedTier) setSessionActiveTier(acceptedTier);
    },
    [],
  );

  // RET-1633 (U10): optimistically settle the trial-cap unlock for this session
  // (see the state declaration). The trial-cap banner container calls this once
  // the UNLOCK modal reaches a terminal success so the just-paid Owner stops
  // seeing the sighting/capped banner before the offer refetch catches up.
  const settleTrialCapUnlock = useCallback(() => {
    setIsTrialCapSettledLocally(true);
  }, []);

  // C7 (RET-1343): persist the vencido-banner dismiss (A10). Optimistically
  // hide this session, then persist; on failure revert the hide and rethrow so
  // the banner surfaces a toast (no silent "it stuck" lie — the persistent
  // `dismissedAt` claim only lands after the POST succeeds).
  const dismissExpired = useCallback(async (): Promise<void> => {
    const workspaceId = activeWorkspaceIdRef.current;
    if (!workspaceId) return;
    setIsResolvedLocally(true);
    try {
      await LeadAgentAcceptanceService.dismissAcceptance(workspaceId);
      if (activeWorkspaceIdRef.current === workspaceId) {
        setAcceptance((prev) =>
          prev ? { ...prev, dismissedAt: new Date().toISOString() } : prev,
        );
      }
    } catch (error) {
      if (activeWorkspaceIdRef.current === workspaceId) {
        setIsResolvedLocally(false);
      }
      throw error;
    }
  }, []);

  const value = useMemo(() => {
    const days = computeDaysUntilPayment(acceptance?.nextPaymentAt ?? null);
    const variant = resolveBannerVariant(days);
    const sinceDays = computeDaysSincePayment(
      acceptance?.nextPaymentAt ?? null,
    );
    // The backend phase owns the access gate. Only older BFF payloads fall back
    // to the UTC-day calculation inside the helper; the display values above
    // remain date-based and therefore unchanged.
    const isExpired = isLeadAgentExpired(acceptance);
    // RET-2153 — terminal, date-independent (see `isLeadAgentRevoked`'s
    // docblock for why this never consults `phase`/`nextPaymentAt`).
    const isRevoked = isLeadAgentRevoked(acceptance);
    const expiredPhase = isExpired ? resolveExpiredPhase(sinceDays) : null;

    // C6 (RET-1342): the pending, pre-charge, eligible set (same set C2 uses,
    // minus the origin suppression). The activation banner partitions this set
    // from C2 by bot state: C6 owns bot-off, C2 owns bot-on.
    // Intentional divergence: this OMITS `isInformativeBannerSuppressed` (which
    // C2 applies). C2 hides the PAID-acceptance offer for the informative
    // variant (>14d) across ALL cohorts (RET-1452); it must NOT hide the
    // config-navigation nudge (C6), so an informative + bot-off owner still
    // sees C6 even though C2 is suppressed.
    const isActivatableTrial =
      isPlanEligible &&
      !isResolvedLocally &&
      isAcceptancePending(acceptance) &&
      !isExpired;

    // C8 (RET-1344): usage/limit banner phase. Mutually exclusive with
    // C2/C6/C7 by data source — `usage` is only ever fetched when there is NO
    // acceptance row (ACCEPTED workspaces 404), so a present `usage` already
    // implies acceptance === null. The explicit guard is belt-and-suspenders.
    //
    // RET-1882 — that data-source argument does NOT extend to the U9 trial
    // family, and reading it as though it did is what shipped two stacked
    // banners. `acceptance === null` is not "never accepted": the endpoint stops
    // returning a row once the tier IS accepted, so a pre-accepted workspace
    // sitting on the trial cap satisfies this branch AND `trialCapVariant` at
    // the same time. Measured on workspace 30522f4d (2026-08-20): entitlement
    // accepted, capacity row still trial-tagged at 104%, both banners painted.
    // The `isTrialCap` argument below is the arbitration; the resolver's
    // docblock carries the authority it comes from.
    // `capped` (>= 100%) takes precedence over `nearLimit` inside
    // resolveUsagePhase. Null usage (loading / error / no cap) → no phase →
    // fail closed (no banner).
    const usagePhase =
      acceptance === null && usage
        ? resolveUsagePhase({
            capacityPct: usage.capacityPct,
            hasCap: usage.hasCap,
            isPaused: usage.isPaused,
            // RET-1882 — the trial family owns a trial-tagged row (U7 flag).
            // See the resolver's docblock for why C8 is the one that cedes.
            isTrialCap: usage.isTrialCap,
          })
        : null;

    // RET-1633 (U9): the trial-cap banner variant, with the R13 hierarchy baked
    // in. The countdown anchor is the OFFER's `trialEnd` (authoritative), not the
    // acceptance `nextPaymentAt` (null for a not-yet-accepted trial). Plan
    // eligibility + the session-resolve latch gate it here (mirroring the other
    // surfaces); never-activated (everUsed === false → C6) and expired (C7) win
    // inside the resolver. `null` while the offer is absent/ineligible → no
    // trial-cap banner (fail closed).
    const trialCapDaysUntilTrialEnd = computeDaysUntilPayment(
      offer?.trialEnd ?? null,
    );
    const trialCapVariant =
      isPlanEligible && !isResolvedLocally && !isTrialCapSettledLocally
        ? resolveTrialCapBannerVariant({
            phase: offer?.eligible ? offer.phase : null,
            daysUntilTrialEnd: trialCapDaysUntilTrialEnd,
            neverActivated: everUsed === false,
            isExpired,
          })
        : null;
    // When the trial-cap banner owns the surface it supersedes the C2
    // pre-acceptance countdown (they share the trial cohort); this subtracts C2
    // only for that cohort, leaving every non-trial-cap workspace unchanged.
    //
    // THIS LINE IS LOAD-BEARING FOR THE ALERT CENTER TOO. `la-acceptance` and
    // `la-trial-cap` are both p1 in the same slot, and `la-acceptance` declares
    // neither an `exclusivityGroup` nor a `suppresses` — so the registry cannot
    // separate them. The only reason an owner in the last days of the trial
    // does not get two alerts asking them to decide the same thing is that this
    // flag turns `isBannerVisible` off, which is what the snapshot reads as
    // `leadAgentAcceptancePending`. Pinned by "never raises the pre-acceptance
    // countdown while the trial cap is live" in the provider tests.
    const trialCapActive = trialCapVariant !== null;
    const acceptedRowTier =
      acceptance?.state === 'ACCEPTED'
        ? resolveSelectableTier(acceptance.tier)
        : null;
    const persistedActiveTier =
      acceptance === null ? resolveSelectableTier(usage?.tier) : null;
    const activeTierForOwner = userIsOwner
      ? (sessionActiveTier ?? acceptedRowTier ?? persistedActiveTier)
      : null;

    return {
      // OWNER-scoped: never expose the (possibly admin-fetched) row to the
      // pill / pre-acceptance / legacy surfaces, which assume non-owner => null.
      acceptance: userIsOwner ? acceptance : null,
      isLoadingAcceptance: userIsOwner ? isLoadingAcceptance : false,
      isAcceptanceSettled: !canSeeLeadAgentSurface || settledWorkspaceId === currentWorkspaceId,
      // Visibility lives here (not in the banner) so the legacy-banner
      // suppression keyed on isBannerVisible stays consistent. OWNER-only, and
      // it cedes once the backend phase reaches trial_ended (`!isExpired`).
      // RET-1490: C2 (acceptance/paywall) is for workspaces that have USED the
      // Lead Agent — it requires `everUsed === true`. Never-used cedes to the C6
      // activation banner. While everUsed is null (loading/unknown) BOTH banners
      // hide (fail-closed).
      isBannerVisible:
        userIsOwner &&
        isPlanEligible &&
        !isResolvedLocally &&
        isAcceptancePending(acceptance) &&
        !isInformativeBannerSuppressed(variant) &&
        !isExpired &&
        everUsed === true &&
        // RET-1633 (U9): the trial-cap banner wins over the C2 countdown for the
        // trial cohort (only subtracts when a trial-cap variant is active).
        !trialCapActive,
      daysUntilPayment: days,
      bannerVariant: variant,
      daysSincePayment: sinceDays,
      expiredPhase,
      // OWNER vencido banner: actionable (reactivate + dismiss). Excludes the
      // retired phase (>30d → Settings only) and a persisted/session dismiss.
      // Gated on plan (RET-1288): the add-on is only purchasable on
      // Pro/Advanced, so a Basic/Legacy owner must never be offered the
      // banner. Do NOT rely on the server to reject the purchase: the
      // reactivation preview has no plan lock (RET-2416), so this gate is the
      // front's half of the rule.
      isExpiredForOwner:
        userIsOwner &&
        isPlanEligible &&
        isExpired &&
        expiredPhase !== 'retired' &&
        !acceptance?.dismissedAt &&
        !isResolvedLocally,
      // OWNER reactivation card in Ajustes → Suscripción (RET-2416): the one
      // door left once the home banner is gone (retired phase or dismissed).
      // Same owner/plan/session-latch as isExpiredForOwner, so a Basic/Legacy
      // or in-trial owner is never offered a purchase their plan forbids.
      isReactivateEntryVisibleForOwner:
        userIsOwner &&
        isPlanEligible &&
        !isResolvedLocally &&
        expiredPhase !== null &&
        (expiredPhase === 'retired' || !!acceptance?.dismissedAt),
      // ADMIN vencido banner: informative only. Mutually exclusive with
      // isBannerVisible by role; ignores dismiss (admin cannot dismiss).
      // Same plan gate (RET-1288) so Basic/Legacy admins never see it either.
      isExpiredForAdmin:
        userIsAdmin &&
        isPlanEligible &&
        isExpired &&
        expiredPhase !== 'retired',
      // RET-2108 / RET-2152 — role-agnostic "no access" gate for the Lead
      // Agent's own config screen. Deliberately NOT gated by
      // `expiredPhase !== 'retired'` (no anti-fatigue clock — that
      // degradation is for the Home banner only), NOT gated by `dismissedAt`
      // (dismissing the Home reminder is not renouncing the purchase path),
      // and NOT role-scoped here (the consuming component decides
      // actionable-vs-informative by role). Still fails closed on plan
      // eligibility and re-arms on any resolve.
      //
      // Two paths reach "no access", because `isLeadAgentExpired` only covers
      // one of the two ways a workspace ends up without the add-on:
      //  - `isExpired` (state 'NONE' past the trial): NEVER accepted the
      //    offer and the trial window ran out.
      //  - `isLeadAgentPaused(acceptance) && isLeadAgentPausedAndLapsed(acceptance)`
      //    (state 'PAUSED' past the cut-off): DID accept, then dropped the
      //    add-on, and the cut-off already passed. `isLeadAgentExpired` short
      //    -circuits on `state !== 'NONE'`, so a paused row never reaches it —
      //    without this second path the block stays invisible forever for
      //    this cohort even though there is no access.
      // Paused-WITHIN-the-trial is deliberately excluded: that cohort still
      // has access until the cut-off (mirrors `isPausedWithinTrialForOwner`,
      // which keeps the shutdown countdown — not this block — on screen for
      // them). RET-2152 (C3) — the second path calls `isLeadAgentPausedAndLapsed`,
      // NOT `!isLeadAgentPausedWithinTrial(acceptance)` AS ITS ONLY EVIDENCE:
      // this gate ENABLES a purchase, so it needs POSITIVE evidence the
      // cut-off passed, the same fail-closed direction `isExpired` already
      // has. Negating `isLeadAgentPausedWithinTrial` as the SOLE term would
      // invert that safety — that helper's `false` on missing data means
      // "cannot confirm still-in-trial", not "confirmed past the cut-off",
      // and `!false` would open this door on a PAUSED row with no `phase`
      // and no `nextPaymentAt` at all (see both helpers' docblocks in
      // LeadAgentAcceptance.functions.ts). Composing the PAUSED helpers here
      // (rather than widening `isLeadAgentExpired` itself) keeps the Home
      // "vencido" banner and alert-center notice — which read
      // `isLeadAgentExpired` directly via `isExpiredForOwner`/`isExpiredForAdmin`/
      // `expiredPhase` — excluding PAUSED, as they must.
      //
      // RET-2152 (vuelta 3, día 0) — `isLeadAgentPausedWithinTrial` (fail-
      // closed toward "still in trial", `daysUntilPayment >= 0`) and
      // `isLeadAgentPausedAndLapsed` (fail-closed toward "not yet lapsed",
      // `daysSincePayment >= 0`) are each individually safe but are NOT
      // complementary: on the cut-off day itself (day 0), with no `phase`,
      // BOTH read true (until === 0 and since === 0 at once). Left alone that
      // opens this purchase gate the same day the shutdown countdown banner
      // (`isPausedWithinTrialForOwner`) promises "still active until the
      // cut-off" — two surfaces, same row, opposite claims, and the false one
      // is the one that charges. The business rule that breaks the tie: a
      // paused add-on stays active THROUGH the cut-off day, so the first
      // day without access is the cut-off day + 1 — unless the backend's
      // `phase` is `trial_ended`, which is authoritative and wins outright.
      // `!isLeadAgentPausedWithinTrial(acceptance)` is added here IN
      // CONJUNCTION with `isLeadAgentPausedAndLapsed`, never as a
      // replacement for it: the positive-evidence term still gates the door,
      // so on missing data (`isLeadAgentPausedAndLapsed` → false) the whole
      // expression still fails CLOSED regardless of what the negated term
      // does. This mirrors the card's `!isPausedWithinTrialForOwner &&
      // isPausedAndLapsedForOwner` guard below (SubscriptionOverview) so the
      // two surfaces can no longer disagree about day 0.
      // RET-2153 (TA-11) — third path: `isRevoked` alone is deliberately NOT
      // enough. Unlike the other two paths (which only need the row's own
      // evidence), a revoked permission is offered again only when today's
      // subscription can still be billed for it (`hasBillableSubscriptionForRevoke`
      // — ACTIVE or PAST_DUE; see `hasBillableSubscriptionForRevokedLeadAgent`'s
      // docblock). That extra cut is scoped to this path ONLY: it does not
      // touch the paused-and-lapsed clause above, which shipped in RET-2152
      // under a different, unmeasured-for-this-cut rule.
      hasNoLeadAgentAccess:
        isPlanEligible &&
        !isResolvedLocally &&
        (isExpired ||
          (isLeadAgentPaused(acceptance) &&
            isLeadAgentPausedAndLapsed(acceptance) &&
            !isLeadAgentPausedWithinTrial(acceptance)) ||
          (isRevoked && hasBillableSubscriptionForRevoke)),
      everUsed,
      // RET-1637 — OWNER dropped the add-on. Two gates, same owner/plan/
      // session-latch as isExpiredForOwner; everUsed is NOT a factor:
      //  - isPausedForOwner: PAUSED regardless of cut-off → the Subscription
      //    reactivation card (reactivation offered before AND after the cut-off).
      //  - isPausedWithinTrialForOwner: PAUSED before phase trial_ended → the
      //    shutdown countdown banner (Home/Embudos). The date shown stays the
      //    existing nextPaymentAt value.
      isPausedForOwner:
        userIsOwner &&
        isPlanEligible &&
        !isResolvedLocally &&
        isLeadAgentPaused(acceptance),
      // RET-2152 (Defecto 2) — role-agnostic "this row is PAUSED" signal, for
      // `LeadAgentNoAccessBlock`, which renders for OWNER *and* ADMIN
      // (`hasNoLeadAgentAccess` is role-agnostic too). `acceptance` itself
      // stays OWNER-scoped (see its docblock above), so an admin consumer
      // cannot read `acceptance.state` directly the way `isPausedForOwner`
      // does; this mirrors that same `isLeadAgentPaused(acceptance)` call
      // without the OWNER gate (deliberately role-agnostic — it backs the
      // admin copy too). PAUSED is decisive, CONFIRMED evidence the workspace
      // once had the add-on and the Owner dropped it — independent of
      // `everUsed` (which only proves the bot replied, not that anyone ever
      // paid). The "never claim a loss it cannot confirm" guard in the
      // block's docblock was written for the cohort that never accepted at
      // all; it does not apply here, because the row itself is the
      // confirmation.
      // RET-2152 (vuelta 3) — DOES carry `isPlanEligible && !isResolvedLocally`,
      // matching every sibling flag below, unlike the previous round. In
      // practice this never changes what the user sees: `isPausedRow` only
      // ever reaches the screen through `LeadAgentNoAccessBlock`'s copy
      // variant, gated behind `hasNoLeadAgentAccess`, which already requires
      // both. The gates are added anyway so this flag can't silently start
      // meaning something looser than its siblings if a future consumer reads
      // it directly without going through `hasNoLeadAgentAccess` first.
      isPausedRow:
        isPlanEligible && !isResolvedLocally && isLeadAgentPaused(acceptance),
      // RET-2153 — role-agnostic "this row is REVOKED" signal, the REVOKED
      // counterpart to `isPausedRow` immediately above. Backs
      // `LeadAgentNoAccessBlock`'s copy variant for BOTH roles (a revoked
      // permission already proves a prior purchase, so it always gets the
      // loss/reactivation copy, never "contrata"). Carries the same
      // `isPlanEligible && !isResolvedLocally` gates as every sibling flag —
      // deliberately NOT the TA-11 billable-subscription cut, which lives in
      // `hasNoLeadAgentAccess` (the gate that actually decides whether
      // anything renders at all); this flag alone never reaches the screen
      // without going through that gate first.
      isRevokedRow: isPlanEligible && !isResolvedLocally && isRevoked,
      // RET-2153 (TA-11) — OWNER + REVOKED + the billable-subscription cut.
      // Mounts the "Activa tu Lead Agent" reactivation card for the revoked
      // cohort alongside `isPausedForOwner`, and its `descriptionLapsed`
      // ("apagado") copy — a revoked row has no pending cut-off, so it must
      // never read "sigue activo hasta la fecha de corte". See
      // `hasBillableSubscriptionForRevokedLeadAgent`'s docblock for why this
      // cut is scoped to REVOKED and intentionally absent from
      // `isPausedForOwner`.
      isRevokedForOwner:
        userIsOwner &&
        isPlanEligible &&
        !isResolvedLocally &&
        isRevoked &&
        hasBillableSubscriptionForRevoke,
      isPausedWithinTrialForOwner:
        userIsOwner &&
        isPlanEligible &&
        !isResolvedLocally &&
        isLeadAgentPausedWithinTrial(acceptance),
      // RET-2152 (vuelta 3, Fix 2) — the SubscriptionOverview counterpart to
      // `isPausedWithinTrialForOwner`, same owner/plan/session-latch gates:
      // POSITIVE evidence the cut-off passed for a PAUSED row (see
      // `isLeadAgentPausedAndLapsed`'s docblock for why this is never derived
      // from `!isPausedWithinTrialForOwner`). Published here — instead of the
      // reactivation card importing `isLeadAgentPausedAndLapsed` and calling
      // it on the raw `acceptance` itself — so the card and this provider's
      // own `hasNoLeadAgentAccess` gate read the SAME computed flag and can no
      // longer diverge on which one applies the day-0 guard.
      //
      // The day-0 guard is composed HERE, not at each call site. The two
      // PAUSED predicates overlap on exactly one input — the cut-off day, where
      // `daysUntilPayment` and `daysSincePayment` are both 0 — so a consumer
      // that read a bare `isLeadAgentPausedAndLapsed` would light the purchase
      // door on the same day the shutdown banner promises the agent still
      // works. Publishing the flag already conjoined means a future third
      // consumer cannot reopen that defect by forgetting the term.
      isPausedAndLapsedForOwner:
        userIsOwner &&
        isPlanEligible &&
        !isResolvedLocally &&
        isLeadAgentPausedAndLapsed(acceptance) &&
        !isLeadAgentPausedWithinTrial(acceptance),
      // RET-1490: C6 activation banner flags. Only when the workspace has NEVER
      // used the Lead Agent (everUsed === false) — this predominates over the
      // days-to-payment variant. null (loading/unknown) keeps both hidden.
      isTrialActivatableForOwner:
        userIsOwner && isActivatableTrial && everUsed === false,
      isTrialActivatableForAdmin:
        userIsAdmin && isActivatableTrial && everUsed === false,
      // C8 (RET-1344): usage/limit banner flags. OWNER = actionable banner,
      // ADMIN = informative pill. Capped and near-limit are mutually exclusive
      // (resolveUsagePhase returns one or null).
      isNearLimitForOwner: userIsOwner && usagePhase === 'nearLimit',
      isNearLimitForAdmin: userIsAdmin && usagePhase === 'nearLimit',
      isCappedForOwner: userIsOwner && usagePhase === 'capped',
      isCappedForAdmin: userIsAdmin && usagePhase === 'capped',
      usageCapacityPct: usage?.capacityPct ?? null,
      usageResetDate: usage?.resetDate ?? null,
      usageTier: usage?.tier ?? null,
      // RET-2226: the same `acceptance === null` cohort `usagePhase` reads.
      paidUsageBand: resolvePaidUsageBand({
        usage,
        hasAcceptanceRow: acceptance !== null,
      }),
      activeTierForOwner,
      // RET-1633 (U9): trial-cap banner flags. Disjoint by construction —
      // `trialCapVariant` is a single value, so at most one (phase × role) flag
      // is set. Owner = actionable, Admin = informative; Seller/viewer get
      // nothing new (the seller-paused banner covers the operational reason).
      isTrialSightingForOwner: userIsOwner && trialCapVariant === 'sighting',
      isTrialSightingForAdmin: userIsAdmin && trialCapVariant === 'sighting',
      isTrialCappedForOwner: userIsOwner && trialCapVariant === 'capped',
      isTrialCappedForAdmin: userIsAdmin && trialCapVariant === 'capped',
      isTrialExtendedCappedForOwner:
        userIsOwner && trialCapVariant === 'extendedCapped',
      isTrialExtendedCappedForAdmin:
        userIsAdmin && trialCapVariant === 'extendedCapped',
      trialCapOffer: offer,
      trialCapDaysUntilTrialEnd,
      usageReadStatus,
      unlockOfferReadStatus,
      usageIsTrialCap: usageReadStatus === 'loaded' && usageIsTrialCap,
      retryTrialCapReads,
      dismissedAt: acceptance?.dismissedAt ?? null,
      acceptTier,
      pauseAcceptance,
      refreshAcceptance,
      markResolved,
      settleTrialCapUnlock,
      hasUnlockedTrialCapThisSession: isTrialCapSettledLocally,
      dismissExpired,
    };
  }, [
    acceptance,
    isLoadingAcceptance,
    canSeeLeadAgentSurface,
    settledWorkspaceId,
    currentWorkspaceId,
    isResolvedLocally,
    isTrialCapSettledLocally,
    isPlanEligible,
    hasBillableSubscriptionForRevoke,
    everUsed,
    usage,
    sessionActiveTier,
    offer,
    usageReadStatus,
    unlockOfferReadStatus,
    usageIsTrialCap,
    retryTrialCapReads,
    userIsOwner,
    userIsAdmin,
    acceptTier,
    pauseAcceptance,
    refreshAcceptance,
    markResolved,
    settleTrialCapUnlock,
    dismissExpired,
  ]);

  return (
    <LeadAgentAcceptanceContext.Provider value={value}>
      {props.children}
    </LeadAgentAcceptanceContext.Provider>
  );
}
